const emptySeverityCounts = () => ({
  critical: 0,
  high: 0,
  medium: 0,
  low: 0,
  unassigned: 0,
  info: 0,
  total: 0
})

const normalizeProjectKey = (name) => String(name || '').trim().toLowerCase()

const toSeverityCounts = (raw) => {
  const base = emptySeverityCounts()
  if (!raw || typeof raw !== 'object') return base
  return {
    critical: Number(raw.critical) || 0,
    high: Number(raw.high) || 0,
    medium: Number(raw.medium) || 0,
    low: Number(raw.low) || 0,
    unassigned: Number(raw.unassigned) || 0,
    info: Number(raw.info) || 0,
    total: Number(raw.total) || 0
  }
}

/** Summary strip / triage — critical → low only */
const stripSeverity = (counts) => ({
  critical: Number(counts?.critical) || 0,
  high: Number(counts?.high) || 0,
  medium: Number(counts?.medium) || 0,
  low: Number(counts?.low) || 0
})

const addSeverityCounts = (a, b) => {
  const left = toSeverityCounts(a)
  const right = toSeverityCounts(b)
  return {
    critical: left.critical + right.critical,
    high: left.high + right.high,
    medium: left.medium + right.medium,
    low: left.low + right.low,
    unassigned: left.unassigned + right.unassigned,
    info: left.info + right.info,
    total: left.total + right.total
  }
}

const compareTriage = (a, b) => {
  const ac = Number(a?.severityCounts?.critical) || 0
  const bc = Number(b?.severityCounts?.critical) || 0
  if (bc !== ac) return bc - ac
  const ah = Number(a?.severityCounts?.high) || 0
  const bh = Number(b?.severityCounts?.high) || 0
  if (bh !== ah) return bh - ah
  const ar = Number(a?.inheritedRiskScore) || 0
  const br = Number(b?.inheritedRiskScore) || 0
  if (br !== ar) return br - ar
  return String(a?.projectName || a?.repositoryName || '').localeCompare(
    String(b?.projectName || b?.repositoryName || '')
  )
}

const projectFromDtRow = (row) => {
  const latest = row?.latestVersion && typeof row.latestVersion === 'object' ? row.latestVersion : {}
  const severityCounts = toSeverityCounts(latest.severityCounts)
  return {
    projectName: row?.projectName || '',
    missingInDt: false,
    versionId: latest.versionId ?? null,
    version: latest.version ?? '',
    lastBomImport: latest.lastBomImport ?? null,
    inheritedRiskScore: Number(latest.inheritedRiskScore) || 0,
    totalFindings: Number(latest.totalFindings) || 0,
    severityCounts,
    availableVersions: Array.isArray(row?.availableVersions) ? row.availableVersions : []
  }
}

const missingDtProject = (projectName, csprojPath = '') => ({
  projectName: projectName || '',
  csprojPath: csprojPath || '',
  missingInDt: true,
  versionId: null,
  version: '',
  lastBomImport: null,
  inheritedRiskScore: 0,
  totalFindings: 0,
  severityCounts: emptySeverityCounts(),
  availableVersions: []
})

/**
 * Join DT /projects with Bitbucket /projects/mappings.
 * Join key: mapping projectName (case-insensitive).
 * Before mappings hydrate, all DT projects are returned as pending orphans.
 */
const buildDependencyTrackViewModel = ({ projects = [], mappings = null, mappingsReady = false } = {}) => {
  const projectList = Array.isArray(projects) ? projects.filter((p) => p && typeof p === 'object') : []
  const byKey = new Map()
  projectList.forEach((row) => {
    const key = normalizeProjectKey(row.projectName)
    if (!key) return
    byKey.set(key, projectFromDtRow(row))
  })

  if (!mappingsReady) {
    const pendingProjects = [...byKey.values()].sort(compareTriage)
    const totals = pendingProjects.reduce(
      (acc, p) => addSeverityCounts(acc, p.severityCounts),
      emptySeverityCounts()
    )
    return {
      mappingsReady: false,
      repositories: [],
      orphans: pendingProjects.map((p) => ({
        ...p,
        pendingMapping: true
      })),
      warnings: [],
      summary: {
        repoCount: 0,
        projectCount: pendingProjects.length,
        orphanCount: pendingProjects.length,
        severity: stripSeverity(totals),
        totalFindings: pendingProjects.reduce((s, p) => s + (Number(p.totalFindings) || 0), 0)
      }
    }
  }

  const repoSource = Array.isArray(mappings?.repositories) ? mappings.repositories : []
  const apiOrphans = Array.isArray(mappings?.orphans) ? mappings.orphans : []
  const warnings = Array.isArray(mappings?.warnings) ? mappings.warnings : []

  const mappedKeys = new Set()
  const repositories = repoSource
    .filter((repo) => repo && typeof repo === 'object')
    .map((repo) => {
      const mappedProjects = Array.isArray(repo.projects) ? repo.projects : []
      const projectsForRepo = mappedProjects
        .filter((p) => p && typeof p === 'object' && p.projectName)
        .map((mapped) => {
          const key = normalizeProjectKey(mapped.projectName)
          mappedKeys.add(key)
          const dt = byKey.get(key)
          if (!dt) {
            return missingDtProject(mapped.projectName, mapped.csprojPath)
          }
          return {
            ...dt,
            csprojPath: mapped.csprojPath || '',
            missingInDt: false
          }
        })
        .sort(compareTriage)

      const severityCounts = projectsForRepo.reduce(
        (acc, p) => addSeverityCounts(acc, p.severityCounts),
        emptySeverityCounts()
      )
      const inheritedRiskScore = projectsForRepo.reduce(
        (max, p) => Math.max(max, Number(p.inheritedRiskScore) || 0),
        0
      )
      const totalFindings = projectsForRepo.reduce(
        (sum, p) => sum + (Number(p.totalFindings) || 0),
        0
      )

      return {
        repositoryName: repo.repositoryName || '',
        repositorySlug: repo.repositorySlug || '',
        repositoryUuid: repo.repositoryUuid || '',
        projects: projectsForRepo,
        severityCounts,
        inheritedRiskScore,
        totalFindings
      }
    })
    .sort(compareTriage)

  const orphanNamesFromApi = new Set(apiOrphans.map(normalizeProjectKey).filter(Boolean))
  const orphans = []

  byKey.forEach((dt, key) => {
    if (mappedKeys.has(key)) return
    orphans.push({ ...dt, pendingMapping: false })
  })

  // API-listed orphans with no DT row still surface as data-gap names
  apiOrphans.forEach((name) => {
    const key = normalizeProjectKey(name)
    if (!key || mappedKeys.has(key) || byKey.has(key)) return
    if (orphanNamesFromApi.has(key) && !orphans.some((o) => normalizeProjectKey(o.projectName) === key)) {
      orphans.push(missingDtProject(name))
    }
  })

  orphans.sort(compareTriage)

  const mappedProjectCount = repositories.reduce((n, r) => n + r.projects.length, 0)
  const severityTotals = [...repositories, ...orphans.filter((o) => !o.missingInDt)].reduce(
    (acc, item) => addSeverityCounts(acc, item.severityCounts),
    emptySeverityCounts()
  )
  // Prefer summing from DT projects once so orphan+mapped don’t double-count
  const fromProjects = projectList.reduce(
    (acc, row) => addSeverityCounts(acc, projectFromDtRow(row).severityCounts),
    emptySeverityCounts()
  )
  const findingsFromProjects = projectList.reduce((s, row) => {
    const latest = row?.latestVersion
    return s + (Number(latest?.totalFindings) || 0)
  }, 0)

  return {
    mappingsReady: true,
    repositories,
    orphans,
    warnings: warnings.map((w) => String(w)).filter(Boolean),
    summary: {
      repoCount: repositories.length,
      projectCount: projectList.length,
      mappedProjectCount,
      orphanCount: orphans.length,
      severity: stripSeverity(fromProjects),
      totalFindings: findingsFromProjects,
      // unused but available if UI needs rolled-up including missing
      severityIncludingGaps: stripSeverity(severityTotals)
    }
  }
}

export {
  emptySeverityCounts,
  normalizeProjectKey,
  toSeverityCounts,
  stripSeverity,
  addSeverityCounts,
  compareTriage,
  buildDependencyTrackViewModel
}
